import { useMutation } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import VirtualCardsBoxAndLinks, {
  VirtualCardsAnalyticsEventProperties,
  VirtualCardsFiltersBody,
  VirtualCardsListContext,
} from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { reportDownloadsMutations } from 'src/queries/reportDownloads/reportDownloads.queries'
import { createSetIsDownloadSuccessPopupOpen } from 'src/redux/actions/app.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import {
  selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFromState,
  selectVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFromState,
  selectVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const AccountsVirtualCards: React.FC = () => {
  const translate = useTranslate()
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const creditCardsActiveDirectEnabledCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount =
    useSelector(
      selectCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFromState,
    )
  const creditCardsActiveCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  )
  const virtualCardsActiveSourceUserOrViewerOrDelegateUserMeCount = useSelector(
    selectVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFromState,
  )
  const virtualCardsCardholderOrViewerMeActiveAndInactiveCount = useSelector(
    selectVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFromState,
  )
  const appName = useSelector(selectAppName)
  const { analytics } = useAnalyticsContext()

  const dispatch = useBackgroundDispatch()
  const reportVirtualCardsDownloadMutation = useMutation(
    reportDownloadsMutations.virtualCards(),
  )

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  const [selectedFilters, setSelectedFilters] = useState<
    VirtualCardsAnalyticsEventProperties | undefined
  >(undefined)

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.VirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatistics,
      appStatisticsKeys.VirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatistics,
    ])
  }, [dispatchStatistics])

  const dispatchPostReportVirtualCardsDownload = (
    filters: VirtualCardsFiltersBody,
  ): void => {
    reportVirtualCardsDownloadMutation.mutate(filters, {
      onSuccess: () => {
        dispatch(createSetIsDownloadSuccessPopupOpen(true))
      },
    })
  }

  const readOnly =
    creditCardsActiveCanIssueVirtualCardsCount === 0 ||
    creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount === 0 ||
    creditCardsActiveDirectEnabledCanIssueVirtualCardsCount === 0

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track('all cards filter applied', {
        card_user:
          selectedFilters.card_user && selectedFilters.card_user.length > 0,
        card_type:
          selectedFilters.card_type && selectedFilters.card_type.length > 0,
        status: selectedFilters.status && selectedFilters.status.length > 0,
        search: !!selectedFilters.search,
        deactivating_range: !!selectedFilters.deactivating_in,
      })
    }
  }, [selectedFilters])

  return !(
    statisticsLoaded.creditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsLoaded &&
    statisticsLoaded.creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsLoaded &&
    statisticsLoaded.creditCardsActiveCanIssueVirtualCardsCountStatisticsLoaded &&
    statisticsLoaded.virtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsLoaded &&
    statisticsLoaded.virtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsLoaded
  ) ? (
    <LoadingBackgroundPage />
  ) : (
    <>
      <Helmet>
        <title>{`Virtual Cards | ${appName}`}</title>
        <meta name="description" content={`Virtual Cards page of ${appName}`} />
      </Helmet>
      <BackgroundPageContent noStackingIndex>
        <VirtualCardsBoxAndLinks
          context={VirtualCardsListContext.Accounts}
          onDownloadClick={dispatchPostReportVirtualCardsDownload}
          downloadPending={reportVirtualCardsDownloadMutation.isPending}
          defaultTitle={
            <ObjectBoxTitle
              title={translate('Virtual Cards Created')}
              static={{
                label: translate('Active'),
                count:
                  virtualCardsActiveSourceUserOrViewerOrDelegateUserMeCount,
              }}
            />
          }
          emptyStateDesign={TableEmptyStateDesigns.AccountVirtualCardsActive}
          totalCount={virtualCardsCardholderOrViewerMeActiveAndInactiveCount}
          readOnly={readOnly}
          setSelectedFilters={setSelectedFilters}
        />
      </BackgroundPageContent>
    </>
  )
}

export default AccountsVirtualCards
